import { Link } from "@tanstack/react-router";
import { ArrowRight } from "lucide-react";

import { useLanguage } from "@/contexts/language";

// Update before the store release if support moves to another address.
// Also used by the landing page's contact section.
export const CONTACT_EMAIL = "contact@iehasa.ge";

interface PolicySection {
  title: string;
  paragraphs: string[];
}

const CONTENT: Record<
  "ar" | "en",
  { title: string; updated: string; intro: string; sections: PolicySection[] }
> = {
  ar: {
    title: "سياسة الخصوصية",
    updated: "آخر تحديث: 3 سبتمبر 2026",
    intro:
      "يساعد تطبيق «مندوب شات» مناديب التوصيل على إشعار المستلمين بشحناتهم والتواصل معهم. توضح هذه السياسة البيانات التي نجمعها وكيفية استخدامها وحمايتها. يُشغَّل التطبيق من قِبل شركة IEHASA المؤسَّسة في جورجيا، وتخضع هذه السياسة لقانون جورجيا لحماية البيانات الشخصية.",
    sections: [
      {
        title: "من يستخدم التطبيق",
        paragraphs: [
          "المندوبون: مندوبو التوصيل الذين يسجّلون الدخول ويستخدمون التطبيق.",
          "المستلمون: الأشخاص الذين تُسلَّم إليهم الشحنات. المستلمون لا يثبّتون التطبيق، بل تصلهم رسائل المندوب في تطبيق المراسلة الذي يستخدمونه أصلاً ويردّون من هناك.",
          "حين تكون شركة التوصيل عميلاً لدينا، فهي المتحكّم في بيانات المستلمين ونحن نعالجها بناءً على تعليماتها.",
        ],
      },
      {
        title: "البيانات التي نجمعها",
        paragraphs: [
          "بيانات الحساب: الاسم ورقم الجوال والبريد الإلكتروني الذي تسجّل به عبر خدمة الدخول «بوابة»، إضافة إلى اسم الشركة واسم المندوب ورقم التواصل التي تُدرَج في رسائلك.",
          "بيانات الشحنات: أسماء المستلمين وأرقام هواتفهم وأرقام التتبع التي تدخلها أو تستوردها من ملف CSV أو Excel. التطبيق لا يقرأ دفتر جهات الاتصال في جهازك.",
          "المحادثات: نصوص الرسائل والوسائط المتبادلة مع المستلمين، وعلامات التسليم والقراءة والتفاعلات.",
          "الموقع: يُطلب إذن الموقع فقط في اللحظة التي تضغط فيها لمشاركة موقع داخل محادثة، ولا نتتبّع موقعك في الخلفية.",
          "بيانات الجهاز: رمز الإشعارات لتنبيهك عند وصول رسالة جديدة من المستلم.",
          "لا نطلب كلمة مرور ولا نخزّنها، ولا نحتفظ بسجلات عناوين IP ولا بسجلات طلباتك داخل التطبيق.",
        ],
      },
      {
        title: "كيف نستخدم البيانات",
        paragraphs: [
          "لتشغيل الخدمة: إنشاء الشحنات وإرسال رسائل القوالب وعرض المحادثات وحالات التسليم.",
          "للتحقق من هويتك والحفاظ على أمان حسابك.",
          "لتنبيهك عند وصول رسالة جديدة من أحد المستلمين.",
          "لاكتشاف إساءة الاستخدام والأعطال التقنية ومنعها.",
          "لا نبيع بياناتك، ولا نستخدم رسائلك أو أرقام المستلمين لأغراض إعلانية، ولا في تدريب نماذج الذكاء الاصطناعي.",
        ],
      },
      {
        title: "الجهات الخارجية",
        paragraphs: [
          "خوادمنا وقاعدة بياناتنا مستضافة ذاتيًا، ولا نستعين بأي مزوّد استضافة أو قواعد بيانات خارجي.",
          "منصة المراسلة: تتلقّى أرقام المستلمين ومحتوى الرسائل والوسائط لإيصالها إلى المستلم.",
          "بوابة (Bawabah): تسجيل الدخول والتحقق من الهوية.",
          "OneSignal: إيصال الإشعارات حين يكون التطبيق مغلقًا.",
        ],
      },
      {
        title: "عن إيصال الرسائل",
        paragraphs: [
          "لا يُوصِّل مندوب شات الرسائل بنفسه، بل يسلّم ما ترسله إلى منصة مراسلة أعمال خارجية تتولّى إيصاله إلى المستلم في تطبيق المراسلة الذي يستخدمه أصلاً، وتخضع تلك المنصة لشروطها وسياسة الخصوصية الخاصة بها. ويرى المستلمون رقم الأعمال لا رقمك الشخصي.",
          "ووفقًا لقواعد تلك المنصة، لا يجوز بدء محادثة إلا مع شخص لديه سبب لتوقّعها — كعميل ينتظر شحنته — وباستخدام قالب رسالة معتمد. ويتحمّل المندوبون وشركات التوصيل مسؤولية امتلاك أساس نظامي لمراسلة كل مستلم.",
        ],
      },
      {
        title: "مدة الاحتفاظ بالبيانات",
        paragraphs: [
          "تُؤرشَف كل محادثة بعد 24 ساعة من فتحها، وبمجرد أرشفتها لا يعود بإمكان المندوب فتحها أو قراءتها في التطبيق. وتبقى المحادثات المؤرشَفة مخزّنة على خوادمنا لأغراض حفظ السجلات وتُحذف عند حذف الحساب.",
          "نحتفظ ببيانات حسابك وسجلات التسليم (رقم التتبع والحالة والأوقات) ما دام حسابك نشطًا.",
        ],
      },
      {
        title: "حقوقك وحذف حسابك",
        paragraphs: [
          "يمكنك الاطلاع على بيانات ملفك وتصحيحها من شاشة الإعدادات، وإيقاف الإشعارات والصوت والاهتزاز، ورفض أذونات الموقع والكاميرا والميكروفون والصور — ويبقى باقي التطبيق يعمل.",
          "ولك طلب نسخة من بياناتك، أو طلب حذفها، أو الاعتراض على معالجة معيّنة أو تقييدها، أو سحب موافقتك.",
          `يمكنك حذف حسابك من الإعدادات ← الحساب ← حذف الحساب، أو بإرسال طلب من بريدك المسجَّل إلى ${CONTACT_EMAIL}. يزيل الحذف ملفك الشخصي ومحادثاتك ورسائلها — بما فيها المؤرشَفة — وسجلات شحناتك. أمّا الرسائل التي وصلت إلى المستلم فتبقى على جهازه ولا يمكننا حذفها.`,
          "نردّ على أي طلب خلال 30 يومًا. ولك الحق في تقديم شكوى إلى خدمة حماية البيانات الشخصية في جورجيا (personaldata.ge) أو إلى الجهة المختصة في بلد إقامتك.",
        ],
      },
      {
        title: "الأمان",
        paragraphs: [
          "تُنقل البيانات مشفّرة عبر HTTPS وتُخزَّن على خوادمنا الخاصة، ويقتصر الوصول إليها على من يحتاجه لتشغيل الخدمة.",
          "يعتمد تسجيل الدخول على رموز لمرة واحدة بدل كلمات مرور مخزّنة، وتُقدَّم الوسائط عبر وسيط وصول مُقيَّد يتحقق من ملكية طالبها للمحادثة.",
        ],
      },
      {
        title: "الأطفال ونقل البيانات",
        paragraphs: [
          "مندوب شات أداة عمل موجَّهة لمندوبي التوصيل المحترفين، ولا نجمع عن قصد بيانات من هم دون 18 عامًا.",
          "قد تعالج منصة المراسلة وبوابة و OneSignal البيانات في دول غير دولتك، ونعتمد حينها على ضمانات النقل التي يوفّرونها كالبنود التعاقدية القياسية.",
        ],
      },
      {
        title: "التغييرات على هذه السياسة",
        paragraphs: [
          "قد نحدّث هذه السياسة من وقت لآخر، وسننشر أي تغيير على هذه الصفحة مع تحديث تاريخ «آخر تحديث».",
        ],
      },
      {
        title: "تواصل معنا",
        paragraphs: [`لأي استفسار حول الخصوصية راسلنا على: ${CONTACT_EMAIL}`],
      },
    ],
  },
  en: {
    title: "Privacy Policy",
    updated: "Last updated: 3 September 2026",
    intro:
      "Mandoob Chat helps delivery drivers notify recipients about their shipments and chat with them. This policy explains what data we collect, how we use it, and how we protect it. The app is operated by IEHASA, a company established in Georgia, and this policy is governed by the Law of Georgia on Personal Data Protection.",
    sections: [
      {
        title: "Who uses the app",
        paragraphs: [
          "Drivers: delivery drivers and couriers who sign in and use the app.",
          "Recipients: the people a driver is delivering a package to. Recipients do not install the app; they receive the driver's messages in the messaging app they already use, and reply there.",
          "Where the delivery company is our customer, the company is the controller of the recipient data and we handle it on the company's instructions.",
        ],
      },
      {
        title: "Data we collect",
        paragraphs: [
          "Account data: the name, phone number and email you sign in with through the Bawabah login service, plus the company name, driver name and contact number that get filled into your messages.",
          "Shipment data: recipient names, phone numbers and tracking numbers you enter or import from a CSV or Excel file. The app does not read your device's address book.",
          "Conversations: the text and media exchanged with recipients, along with delivery and read receipts and reactions.",
          "Location: access is requested only at the moment you tap to share a location in a chat. We do not track your position in the background.",
          "Device data: a push notification identifier used to alert you when a recipient replies with a new message.",
          "We do not ask for or store your password, and we do not keep IP address logs or request logs of your activity in the app.",
        ],
      },
      {
        title: "How we use it",
        paragraphs: [
          "To run the service: creating deliveries, sending template messages, and showing conversations and delivery statuses.",
          "To authenticate you and keep your account secure.",
          "To notify you when a recipient sends you a new message.",
          "To detect and prevent abuse and technical faults.",
          "We never sell your data, never use your messages or recipients' numbers for advertising, and never use them to train machine-learning models.",
        ],
      },
      {
        title: "Third parties",
        paragraphs: [
          "Our servers and database are self-hosted; we do not use a third-party hosting or database provider.",
          "The messaging platform: receives recipient phone numbers, message content and media in order to deliver your messages.",
          "Bawabah: sign-in and identity verification.",
          "OneSignal: delivering notifications when the app is closed.",
        ],
      },
      {
        title: "About message delivery",
        paragraphs: [
          "Mandoob Chat does not deliver messages itself. Messages you send are handed to an external business messaging platform, which delivers them to the recipient in the messaging app they already use, subject to that platform's own terms and privacy policy. Recipients see the business number, not your personal number.",
          "Under that platform's rules, a business may only start a conversation with someone who has a reason to expect it — such as a customer awaiting a delivery — and only using an approved message template. Drivers and delivery companies are responsible for having a lawful basis to contact each recipient.",
        ],
      },
      {
        title: "Retention",
        paragraphs: [
          "Every conversation is archived 24 hours after it is opened. Once archived, the driver can no longer open or read it in the app. Archived conversations remain stored on our servers for record-keeping and are deleted when the account is deleted.",
          "We keep your account data and delivery records (tracking number, status, timestamps) while your account is active.",
        ],
      },
      {
        title: "Your rights and account deletion",
        paragraphs: [
          "You can see and correct your profile data in Settings, turn off notifications, sound and haptics, and refuse location, camera, microphone and photo access — the rest of the app keeps working.",
          "You can also request a copy of your data, request its deletion, object to or restrict certain processing, and withdraw consent.",
          `You can delete your account from Settings → Account → Delete account, or by sending a request from your registered address to ${CONTACT_EMAIL}. Deletion removes your profile, your conversations and their messages — archived ones included — and your delivery records. Messages already delivered to a recipient remain on their device; we cannot remove those.`,
          "We respond to any request within 30 days. You also have the right to complain to the Personal Data Protection Service of Georgia (personaldata.ge), or to the authority in the country you live in.",
        ],
      },
      {
        title: "Security",
        paragraphs: [
          "Data travels encrypted over HTTPS and is stored on our own servers, with access limited to what running the service requires.",
          "Sign-in uses one-time codes rather than stored passwords, and media is served through an access-controlled proxy that checks the requester owns the conversation.",
        ],
      },
      {
        title: "Children and international transfers",
        paragraphs: [
          "Mandoob Chat is a workplace tool for professional delivery drivers; we do not knowingly collect data from anyone under 18.",
          "The messaging platform, Bawabah and OneSignal may process data in countries other than yours. Where data leaves your country we rely on the transfer safeguards those providers offer, such as standard contractual clauses.",
        ],
      },
      {
        title: "Changes to this policy",
        paragraphs: [
          'We may update this policy from time to time; changes are published on this page with an updated "last updated" date.',
        ],
      },
      {
        title: "Contact us",
        paragraphs: [`For any privacy question, email us at: ${CONTACT_EMAIL}`],
      },
    ],
  },
};

/** Public privacy policy at /privacy — required for the store listings. */
export function PrivacyPage() {
  const { t, dir, language } = useLanguage();
  const content = CONTENT[language];

  return (
    <div dir={dir} className="tw:min-h-dvh tw:bg-background">
      <div className="tw:mx-auto tw:flex tw:w-full tw:max-w-3xl tw:flex-col tw:gap-8 tw:px-6 tw:pb-16 tw:pt-[calc(32px+env(safe-area-inset-top))] tw:md:pt-12">
        <header className="tw:flex tw:flex-col tw:gap-3">
          <Link
            to="/"
            className="tw:inline-flex tw:w-fit tw:items-center tw:gap-1.5 tw:text-sm tw:font-medium tw:text-brand tw:no-underline tw:hover:underline"
          >
            <ArrowRight className="tw:size-4 tw:ltr:-scale-x-100" />
            {t("Mandoob:AppName")}
          </Link>
          <h1 className="tw:m-0 tw:text-3xl tw:font-bold tw:text-foreground">
            {content.title}
          </h1>
          <p className="tw:m-0 tw:text-sm tw:text-muted-foreground">
            {content.updated}
          </p>
        </header>

        <p className="tw:m-0 tw:text-base tw:leading-relaxed tw:text-foreground">
          {content.intro}
        </p>

        {content.sections.map((section) => (
          <section key={section.title} className="tw:flex tw:flex-col tw:gap-2">
            <h2 className="tw:m-0 tw:text-xl tw:font-semibold tw:text-foreground">
              {section.title}
            </h2>
            {section.paragraphs.map((paragraph) => (
              <p
                key={paragraph}
                className="tw:m-0 tw:text-sm tw:leading-relaxed tw:text-muted-foreground"
              >
                {paragraph}
              </p>
            ))}
          </section>
        ))}
      </div>
    </div>
  );
}
